// React import
import React, { useState, useMemo } from 'react';

// Material UI import
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import CircularProgress from '@mui/material/CircularProgress';
import * as colors from '@mui/material/colors';
import { useTheme } from '@mui/material/styles';

// Generic imports
import _ from 'lodash';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';

// Font Awesome import
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as icons from '@fortawesome/pro-light-svg-icons';
import { 
    faChartLineUp as faChartLineUpRegular, faChartLineDown as faChartLineDownRegular,
} from '@fortawesome/pro-solid-svg-icons';

// Local imports
import { calcChargeableWeight, calcWeightOrMeasurment } from '../../CalcWeight.js';
import CurrencyToText from '../../CurrencyToText.js';
import { opportunitySellAndCost } from '../../utils/opportunityFinancials.js';
import OpportunitiesFilter from '../components/OpportunitiesFilter.js';

import isoWeek from 'dayjs/plugin/isoWeek';
dayjs.extend(weekday);
dayjs.extend(isoWeek);

const toDayjsUnit = (p) => p === 'week' ? 'isoWeek' : p;

function HomeKPIs(props) {
    const { org, navigate, me, reportConfiguration, accounts, opportunities, exchangeRates } = props;
    const [ tab, setTab ] = useState(0);
    const theme = useTheme();
    const [ filter, setFilter ] = useState((() => {
        const filterStorage = sessionStorage.getItem('HomeKPIs.filter');
        const base = { days: 'week', teamMember: me.email, accounts: [], priceQuote: 'all', rateRequest: 'all', taskStatus: 'all', taskType: 'one-off', tags: [], freetext: '' };
        try {
            return filterStorage ? JSON.parse(filterStorage) : base;
        } catch (e) {
            return base;
        }
    }));

    const since = useMemo(() => getStartOfTimePeriod({ timePeriod: filter.days, dateFrom: filter.dateFrom }), [filter.days, filter.dateFrom]);
    const until = useMemo(() => getEndOfTimePeriod({ timePeriod: filter.days, dateTo: filter.dateTo }), [filter.days, filter.dateTo]);
    const previousTimePeriod = useMemo(() => getPreviousTimePeriod(filter.days), [filter.days]);

    function setFilterWithStorage(newFilter) {
        if (typeof newFilter === 'function') {
            setFilter(prev => {
                const resolved = newFilter(prev);
                sessionStorage.setItem('HomeKPIs.filter', JSON.stringify(resolved));
                return resolved;
            });
        } else {
            sessionStorage.setItem('HomeKPIs.filter', JSON.stringify(newFilter));
            setFilter(newFilter);
        }
    }

    const filteredOpportunities = (opportunities || []).filter(r => r.status !== 'deleted');

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '24px' }}>
            <Paper sx={{ padding: '24px', margin: '0px 24px', flex: 1, background: theme.palette.panel1, border: `1px solid ${theme.palette.border1}` }} elevation={0}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
                    <Typography variant='body2' component={Box} sx={{ fontWeight: 800, flex: 1, paddingBottom: '28px', borderBottom: `1px solid ${colors.grey[300]}` }}>
                        Performance Overview
                    </Typography>
                    <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ borderBottom: `1px solid ${colors.grey[300]}` }}>
                        {(reportConfiguration || []).map((page, pageIndex) => (
                            <Tab label={page.title} index={pageIndex} />
                        ))}
                    </Tabs>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                    <OpportunitiesFilter
                        filter={filter}
                        setFilter={setFilterWithStorage}
                        me={me}
                        org={org}
                        opportunities={filteredOpportunities}
                        accounts={accounts}
                        groupedDays={true}
                        hideTaskType={true}
                        hideRoutes={true}
                        hideTaskStatus={true}
                        hideSearch={true}
                        hidePipeline={true}
                    />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'stretch', marginBottom: '12px', gap: '12px', width: '100%' }}>
                    {(reportConfiguration || [])[tab]?.fields?.map((field, fieldIndex) => {
                        const value = (() => {
                            if (!field.func) return '';
                            try {
                                const func = new Function('accounts', 'opportunities', 'since', 'until', 'prevSince', 'prevUntil', 'getOpportunityProducts', 'getOpportunityBuyingPrice', 'getOpportunitySalePrice', 'exchangeRates', 'amountToStr', 'filter', field.func);
                                return func(
                                    accounts,
                                    filteredOpportunities,
                                    since,
                                    until,
                                    previousTimePeriod?.since,
                                    previousTimePeriod?.until,
                                    (opportunity) => {
                                        if (!opportunity.products) return {};
                                        const sums = opportunity.products.map((p, i) => {
                                            const CW = calcChargeableWeight({
                                                product: p, transportationMethod: opportunity.transportationMethod, allProducts: opportunity.products, round: org.airRoundChargeableWeight
                                            });
                                            if (CW) return { type: 'chargeable Weight', value: CW }

                                            const WM = calcWeightOrMeasurment({
                                                product: p, transportationMethod: opportunity.transportationMethod, allProducts: opportunity.products
                                            });
                                            if (WM) return { type: 'weight or measurement', value: WM };

                                            // A product matching none of the cargo types above (e.g. an
                                            // Air freight product on a sea quote) has no container quantity
                                            // — the unified 'Air freight'/'LCL' types keep it inside
                                            // calculations[]. Skip it rather than counting it as a container
                                            // with no value, which made sumBy return undefined.
                                            if (_.isUndefined(p.quantity)) return null;
                                            return { type: 'Containers', value: { val: p.quantity, postfix: '' } };
                                        });
                                        return _(sums).compact().groupBy(s => `${s.value.postfix ? s.value.postfix + ' ' : ''} ${s.type}`).
                                            mapValues(v => _.sumBy(v, v2 => v2.value.val || 0)).
                                            value();
                                    },
                                    // The quote's own Cost and Sell (opportunitySellAndCost), as on its page and in
                                    // Quotation Analytics: one amount in the organization's currency, in the
                                    // { currency: amount } shape the report formulas expect.
                                    (opportunity) => {
                                        const fin = opportunitySellAndCost({ opportunity, exchangeRates, organization: org });
                                        return fin && isFinite(fin.cost) ? { [org?.defaults?.currency || 'USD']: fin.cost } : {};
                                    },
                                    (opportunity) => {
                                        const fin = opportunitySellAndCost({ opportunity, exchangeRates, organization: org });
                                        return fin && isFinite(fin.sell) ? { [org?.defaults?.currency || 'USD']: fin.sell } : {};
                                    },
                                    exchangeRates,
                                    (amount, currency) => {
                                        return CurrencyToText(currency, true, amount);
                                    },
                                    {
                                        members: _.isEmpty(filter.members) ? null : filter.members,
                                    },
                                );
                            } catch (e) {
                                console.error(e);
                                return '';
                            }
                        })();
                        const caption = (() => {
                            if (!previousTimePeriod) return '';
                            if (value?.caption !== 'comparison') return value?.caption;
                            const change = value.prevPeriodStats === 0 ? 0 : ((value.mainStats - value.prevPeriodStats) / value.prevPeriodStats) * 100;
                            return getCaption({
                                change: !change && value.mainStats > 0 ? 'inf' : change,
                                previousTimePeriod,
                            });
                        })();
                        return (
                            <HomeSingleKPI
                                title={field.title}
                                value={value?.mainStats}
                                caption={caption}
                                icon={icons[field.icon]}
                                color={colors[field.iconColor]}
                                onHoverText={field.onHoverText}
                                onClick={field.navigateLink ? () => navigate(field.navigateLink) : null}
                            />
                        );
                    })}
                </Box>
            </Paper>
        </Box>
    );
}

function HomeSingleKPI(props) {
    const { thinking, title, value, caption, icon, color, onHoverText, onClick } = props;
    const [ hover, setHover ] = useState(false);
    const theme = useTheme();

    if (thinking) {
        return (
            <Box
                sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '24px',
                    border: `1px solid ${theme.palette.border1}`,
                    borderRadius: '6px',
                    background: theme.palette.panel1,
                    ...(hover && { border: `1px solid ${colors.blue[500]}` }),
                }}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
            >
                <Typography variant='body2' component={Box} sx={{ flex: 1 }}>
                    {title}
                </Typography>
                <Typography variant='body2' component={Box} sx={{ flex: 1, textAlign: 'center', padding: '17px 0px' }}>
                    <CircularProgress />
                </Typography>
            </Box>
        );
    }
    return (
        <Box 
            sx={{ 
                position: 'relative',
                display: 'flex', 
                flexDirection: 'column', 
                padding: '24px', 
                border: `1px solid ${theme.palette.border1}`, 
                borderRadius: '6px', 
                justifyContent: 'space-between',
                background: theme.palette.panel1,
                minHeight: '180px',
                flex: 1,
                ...(hover && { border: `1px solid ${colors.blue[500]}` }),
                ...(onClick && { cursor: 'pointer' })
            }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onClick={onClick}
        >
            <Box sx={{ display: 'flex', marginBottom: '6px', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <Typography variant='body2' component={Box} sx={{ flex: 1 }}>
                        {title}
                    </Typography>
                    <Typography variant='h6' component={Box} sx={{ fontWeight: 800, flex: 1 }}>
                        {value}
                    </Typography>
                </Box>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: color[100],
                    color: color[500],
                    width: '50px',
                    height: '50px',
                    borderRadius: '12px',
                    marginLeft: '12px',
                    fontSize: '1.2em'
                }}>
                    <FontAwesomeIcon icon={icon} />
                </Box>
            </Box>
            <Typography variant='caption' component={Box} sx={{ color: colors.grey[500] }}>
                {caption}
            </Typography>
            {hover && onHoverText && (
                <Box sx={{ position: 'absolute', top: '0px', bottom: '0px', left: '0px', right: '0px', background: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant='caption' component={Box} sx={{ color: theme.palette.text.primary, fontWeight: 800 }}>
                        {onHoverText}
                    </Typography>
                </Box>
            )}
        </Box>
    )
}

export default HomeKPIs;

function getStartOfTimePeriod({ timePeriod, dateFrom }) {
    if (timePeriod === 'all') {
        return 0;
    }
    if (timePeriod === 'custom') {
        return dayjs(dateFrom).startOf('day').valueOf();
    }
    if (timePeriod.startsWith('past-')) {
        const period = timePeriod.split('-')[1];
        return dayjs().subtract(1, period).startOf(toDayjsUnit(period)).valueOf();
    }
    return dayjs().startOf(toDayjsUnit(timePeriod)).valueOf();
}

function getEndOfTimePeriod({ timePeriod, dateTo }) {
    if (timePeriod === 'all') {
        return dayjs().endOf('day').valueOf();
    }
    if (timePeriod === 'custom') {
        return dayjs(dateTo).endOf('day').valueOf();
    }

    if (timePeriod.startsWith('past-')) {
        const period = timePeriod.split('-')[1];
        return dayjs().subtract(1, period).endOf(toDayjsUnit(period)).valueOf();
    }
    return dayjs().endOf(toDayjsUnit(timePeriod)).valueOf();
}

function getPreviousTimePeriod(timePeriod) {
    if (timePeriod === 'week') {
        return {
            since: dayjs().subtract(1, 'week').startOf('isoWeek').valueOf(),
            until: dayjs().subtract(1, 'week').endOf('isoWeek').valueOf(),
            text: 'from last week',
        }
    }
    if (timePeriod === 'month') {
        return {
            since: dayjs().subtract(1, 'month').startOf('month').valueOf(),
            until: dayjs().subtract(1, 'month').endOf('month').valueOf(),
            text: 'from last month',
        }
    }   
    if (timePeriod === 'quarter') {
        return {
            since: dayjs().subtract(1, 'quarter').startOf('quarter').valueOf(),
            until: dayjs().subtract(1, 'quarter').endOf('quarter').valueOf(),
            text: 'from last quarter',
        }
    }

    if (timePeriod === 'past-week') {
        return {
            since: dayjs().subtract(2, 'week').startOf('isoWeek').valueOf(),
            until: dayjs().subtract(1, 'week').endOf('isoWeek').valueOf(),
            text: 'from 2 weeks ago',
        }
    }
    if (timePeriod === 'past-month') {
        return {
            since: dayjs().subtract(2, 'month').startOf('month').valueOf(),
            until: dayjs().subtract(1, 'month').endOf('month').valueOf(),
            text: 'from 2 months ago',
        }
    }

    if (timePeriod === 'past-quarter') {
        return {
            since: dayjs().subtract(2, 'quarter').startOf('quarter').valueOf(),
            until: dayjs().subtract(1, 'quarter').endOf('quarter').valueOf(),
            text: 'from 2 quarters ago',
        }
    }

    return null;
}

function getCaption({ change, previousTimePeriod }) {
    if (change === 'inf') {
        return (
            <div>
                <span style={{ color: colors.green[500], fontWeight: 800 }}>
                    <FontAwesomeIcon style={{ fontSize: '1.2em' }} icon={faChartLineUpRegular} /> Inf
                </span> up {previousTimePeriod.text}
            </div>
        );
    }
    if (change === 0) {
        return <div>No change</div>;
    }
    return (
        <div>
            <span style={{ color: change > 0 ? colors.green[500] : colors.red[500], fontWeight: 800 }}>
                <FontAwesomeIcon style={{ fontSize: '1.2em' }} icon={change > 0 ? faChartLineUpRegular : faChartLineDownRegular} /> {Math.abs(change).toFixed(2)}%
            </span> {change > 0 ? 'up' : 'down'} {previousTimePeriod.text}
        </div>
    );
}